// SPV Pool v1.0 PR 5 — TanStack Query hooks for the Investor surface.
// Follows the useSpvPools pattern: query-key factory + thin wrappers.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";
import {
  investorCommitmentsApi,
  investorDistributionsApi,
  investorsApi,
} from "@/api/investors";
import type {
  InvestorCommitmentCreate,
  InvestorCommitmentListQuery,
  InvestorCreate,
  InvestorDistributionListQuery,
  InvestorListQuery,
  InvestorUpdate,
} from "@/types/investors";

export const investorKeys = {
  all: ["investors"] as const,
  lists: () => [...investorKeys.all, "list"] as const,
  list: (query: InvestorListQuery | undefined) =>
    [...investorKeys.lists(), query ?? {}] as QueryKey,
  detail: (id: string) => [...investorKeys.all, "detail", id] as const,
  commitmentsPrefix: () => [...investorKeys.all, "commitments"] as const,
  commitments: (query: InvestorCommitmentListQuery | undefined) =>
    [...investorKeys.commitmentsPrefix(), query ?? {}] as QueryKey,
  distributions: (query: InvestorDistributionListQuery | undefined) =>
    [...investorKeys.all, "distributions", query ?? {}] as QueryKey,
};

export function useInvestorList(query?: InvestorListQuery, enabled = true) {
  return useQuery({
    queryKey: investorKeys.list(query),
    queryFn: () => investorsApi.list(query),
    enabled,
  });
}

export function useInvestor(id: string | undefined) {
  return useQuery({
    queryKey: investorKeys.detail(id ?? ""),
    queryFn: () => investorsApi.get(id!),
    enabled: !!id,
  });
}

/**
 * Lazy, audited reveal of the plaintext sensitive fields. Modeled as a
 * mutation — never a cached query — so the plaintext is never written to
 * the React Query cache. The caller copies the result into local state
 * and calls {@link reset} (via mutation.reset) on Hide / unmount.
 */
export function useRevealInvestorSensitive(id: string) {
  return useMutation({
    mutationFn: () => investorsApi.getSensitive(id),
  });
}

export function useCreateInvestor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InvestorCreate) => investorsApi.create(body),
    onSuccess: (investor) => {
      qc.invalidateQueries({ queryKey: investorKeys.lists() });
      qc.setQueryData(investorKeys.detail(investor.id), investor);
    },
  });
}

export function useUpdateInvestor(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InvestorUpdate) => investorsApi.update(id, body),
    onSuccess: (investor) => {
      qc.setQueryData(investorKeys.detail(id), investor);
      qc.invalidateQueries({ queryKey: investorKeys.lists() });
    },
  });
}

export function useInvestorCommitments(
  query?: InvestorCommitmentListQuery,
  enabled = true,
) {
  return useQuery({
    queryKey: investorKeys.commitments(query),
    queryFn: () => investorCommitmentsApi.list(query),
    enabled,
  });
}

export function useInvestorDistributions(
  query?: InvestorDistributionListQuery,
  enabled = true,
) {
  return useQuery({
    queryKey: investorKeys.distributions(query),
    queryFn: () => investorDistributionsApi.list(query),
    enabled,
  });
}

export function useCreateCommitment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InvestorCommitmentCreate) =>
      investorCommitmentsApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: investorKeys.commitmentsPrefix() });
    },
  });
}
